import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  ApiResponse,
  OrganizationContactResponse,
} from "../../types/certWatch";
import { getCCOsByOrgIdAndFlag } from "../thunks/ccoAdvanceThunk";

interface CertConnectState {
  ccoList: OrganizationContactResponse[];
  Loading: boolean;
  error: string | null;
  activeFlag: number;
  PageNumber: number;
  PageSize: number;
  totalPages: number;
  totalCertifiedCount: number;
  sortBy: string;
  sortOrder: "asc" | "desc";
  availability: boolean | null; 
  certExpirationDate: string | null; 
  certExpirationDateMode: "on" | "onorbefore" | "onorafter" | "";
  designationFilterMode: "any" | "all";
  selectedDesignations: string[];
  designationSearchTerm: string;
  notesSearchTerm: string | null;  
  FirstNameSearch : string | null;  
  LastNameSearch : string | null;        
  CcoIdSearchTerm : string | null;  
      ContactMobilePhone: string;
      ContactEmail: string;
  appliedFilters: {
    availability: boolean;
    certExpirationDate: boolean;
    notesSearchTerm: boolean;
    FirstNameSearch: boolean;
    LastNameSearch: boolean;
    CcoIdSearchTerm: boolean;
    selectedDesignations: boolean;
  };
}

const initialState: CertConnectState = {
  ccoList: [],
  Loading: false,
  error: null,
  activeFlag: 1,
  PageNumber: 1, // 1-indexed for API
  totalPages: 0,
  PageSize: 25,
  totalCertifiedCount: 0,
  sortBy: "LastName",
  sortOrder: "asc",
  availability: null,
  certExpirationDate: null,
  certExpirationDateMode: "",
  designationFilterMode: "any",
  selectedDesignations: [],
  designationSearchTerm: "",
  notesSearchTerm: "",
  FirstNameSearch: "",
  LastNameSearch: "",
  CcoIdSearchTerm: "",
  ContactMobilePhone: "",
  ContactEmail: "",
  appliedFilters: {
    availability: false,
    certExpirationDate: false,
    notesSearchTerm: false,
    FirstNameSearch: false,
    LastNameSearch: false,
    CcoIdSearchTerm: false,
    selectedDesignations: false
  }
};

const CCoAdvanceSlice = createSlice({
  name: "ccoAdvance",
  initialState,
  reducers: {
    reset(state) {
      state.ccoList = [];
      state.Loading = false;
      state.error = null;
      state.PageNumber = 1; // Reset to 1 (API is 1-indexed)
      state.totalPages = 0;
    },
    setActiveFlag(state, action: PayloadAction<number>) {
      state.activeFlag = action.payload;
      state.ccoList = [];
    },
    setPage(state, action: PayloadAction<number>) {
      state.PageNumber = action.payload;
    },
    setActiveFlagAndPage(
      state,
      action: PayloadAction<{ flag: number; page: number }>
    ) {
      state.activeFlag = action.payload.flag;
      state.PageNumber = action.payload.page;
    },

    setdPageAndPageSize(
      state,
      action: PayloadAction<{ Page: number; PageSize: number }>
    ) {
      state.PageNumber = action.payload.Page;
      state.PageSize = action.payload.PageSize;
    },
    setPageSize(state, action: PayloadAction<number>) {
      state.PageSize = action.payload;
      state.PageNumber = 1; 
    },
    setSort: (state, action: PayloadAction<{ sortBy: string; sortOrder: 'asc' | 'desc' }>) => {
  state.sortBy = action.payload.sortBy;
  state.sortOrder = action.payload.sortOrder;

},

    setAvailability(state, action: PayloadAction<boolean | null>) {
      state.availability = action.payload;
      state.PageNumber = 1; 
    },
    
    setCertExpirationFilter(
      state,
      action: PayloadAction<{
        date: string | null;
        mode: "on" | "onorbefore" | "onorafter" | "";
      }>
    ) {
      state.certExpirationDate = action.payload.date;
      state.certExpirationDateMode = action.payload.mode;
      state.PageNumber = 1; // reset paging
    },

    clearCertExpirationFilter(state) {
      state.certExpirationDate = null;
      state.certExpirationDateMode = "";
      state.PageNumber = 1;
    },
    setDesignationFilterMode(state, action: PayloadAction<"any" | "all">) {
      state.designationFilterMode = action.payload;
      state.PageNumber = 1;
    },

    setSelectedDesignations(state, action: PayloadAction<string[]>) {
      state.selectedDesignations = action.payload;
      state.PageNumber = 1;
    },

    setDesignationSearchTerm(state, action: PayloadAction<string>) {
      state.designationSearchTerm = action.payload;
      state.PageNumber = 1; // reset to first page when filter changes
    },
  setNotesSearchTerm(state, action: PayloadAction<string>) {
    state.notesSearchTerm = action.payload;
    state.PageNumber = 1; // reset to first page when filter changes
  },
   setFirstNameSearchTerm(state, action: PayloadAction<string>) {
    state.FirstNameSearch = action.payload;
    state.PageNumber = 1; // reset to first page when filter changes
  },
    setLastNameSearchTerm(state, action: PayloadAction<string>) {
    state.LastNameSearch = action.payload;
    state.PageNumber = 1; // reset to first page when filter changes
  },
   setCcoIdSearchTerm(state, action: PayloadAction<string>) {
    state.CcoIdSearchTerm = action.payload;
    state.PageNumber = 1; // reset to first page when filter changes
  },
    resetDesignationFilters(state) {
      state.designationFilterMode = "any";
      state.selectedDesignations = [];
      state.designationSearchTerm = "";
      state.PageNumber = 1;
    },
  setAppliedFilter(state, action: PayloadAction<{ filter: keyof CertConnectState['appliedFilters'], value: boolean }>) {
  state.appliedFilters[action.payload.filter] = action.payload.value;
}

  },
  extraReducers: (builder) => {
    builder
      .addCase(getCCOsByOrgIdAndFlag.pending, (state) => {
        state.Loading = true;
        state.error = null;
      })
      .addCase(
        getCCOsByOrgIdAndFlag.fulfilled,
        (
          state,
          action: PayloadAction<ApiResponse<OrganizationContactResponse>>
        ) => {
          state.Loading = false;
          state.ccoList = action.payload.Data.Items; // use Items array
          state.totalCertifiedCount = action.payload.Data.TotalCount;
          state.totalPages = Math.ceil(
            action.payload.Data.TotalCount / state.PageSize
          );
        }
      )

      .addCase(getCCOsByOrgIdAndFlag.rejected, (state, action) => {
        state.Loading = false;
        state.error = action.payload
          ? (action.payload as string)
          : action.error.message || "Failed to fetch CCOs";
      });
  },
});

export const {
  reset,
  setActiveFlag,
  setdPageAndPageSize,
  setPage,
  setPageSize,
  setActiveFlagAndPage,
  setAvailability,
  setCertExpirationFilter,
  clearCertExpirationFilter,
  setDesignationFilterMode,
  setSelectedDesignations,
  setDesignationSearchTerm,
  resetDesignationFilters,
    setNotesSearchTerm, 
    setFirstNameSearchTerm,
    setLastNameSearchTerm,
    setCcoIdSearchTerm, 
    setSort,setAppliedFilter
} = CCoAdvanceSlice.actions;
export default CCoAdvanceSlice.reducer;
